import { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';

const safeStringify = (obj: any): string => {
    try {
        return JSON.stringify(obj);
    } catch (e) {
        console.error("[useSyncState] JSON.stringify failed. Payload might be too large or contain circular references.", e);
        return "";
    }
};

interface SyncQueueItem {
    collectionName: string;
    docId: string;
    payload: any;
    timestamp: string;
}

// Helper to check if error indicates a network/connectivity issue
const isNetworkError = (error: any): boolean => {
    if (!error) return false;
    const msg = (error.message || "").toLowerCase();
    return (
        msg.includes("fetch") || 
        msg.includes("network") || 
        msg.includes("timeout") || 
        msg.includes("failed to fetch") || 
        error.status === 0 || 
        error.code === "PGRST301" || 
        error.code === "TypeError"
    );
};

// Queue handling: Enqueue item for offline syncing
const enqueueOfflineSync = (collectionName: string, docId: string, payload: any) => {
    try {
        const queueStr = localStorage.getItem("klyd_offline_sync_queue");
        let queue: SyncQueueItem[] = queueStr ? JSON.parse(queueStr) : [];
        // Remove any existing entry for the same doc
        queue = queue.filter(item => !(item.collectionName === collectionName && item.docId === docId));
        // Add new entry
        queue.push({
            collectionName,
            docId,
            payload,
            timestamp: new Date().toISOString()
        });
        localStorage.setItem("klyd_offline_sync_queue", JSON.stringify(queue));
        console.log(`[Offline Queue] Enqueued sync for ${collectionName}/${docId}. Queue size: ${queue.length}`);
    } catch (e) {
        console.error("[Offline Queue] Error enqueueing:", e);
    }
};

// Queue handling: Dequeue item once successfully synced
const dequeueOfflineSync = (collectionName: string, docId: string) => {
    try {
        const queueStr = localStorage.getItem("klyd_offline_sync_queue");
        if (!queueStr) return;
        let queue: SyncQueueItem[] = JSON.parse(queueStr);
        const originalLength = queue.length;
        queue = queue.filter(item => !(item.collectionName === collectionName && item.docId === docId));
        if (queue.length !== originalLength) {
            localStorage.setItem("klyd_offline_sync_queue", JSON.stringify(queue));
            console.log(`[Offline Queue] Dequeued sync for ${collectionName}/${docId}. Queue size: ${queue.length}`);
        }
    } catch (e) {
        console.error("[Offline Queue] Error dequeueing:", e);
    }
};

let isSyncingQueue = false;

// Attempt to sync all pending offline queue items
const syncOfflineQueue = async () => {
    if (isSyncingQueue) return;
    try {
        const queueStr = localStorage.getItem("klyd_offline_sync_queue");
        if (!queueStr) return;
        const queue: SyncQueueItem[] = JSON.parse(queueStr);
        if (queue.length === 0) return;

        if (typeof navigator !== "undefined" && !navigator.onLine) {
            console.log("[Offline Queue] WebView reports offline status. Retrying sync skipped.");
            return;
        }

        isSyncingQueue = true;
        console.log(`[Offline Queue] Triggered retry for ${queue.length} pending items...`);

        for (const item of queue) {
            console.log(`[Offline Queue] Retrying upload for ${item.collectionName}/${item.docId}...`);
            const { error } = await supabase
                .from('app_data')
                .upsert({
                    collection_name: item.collectionName,
                    doc_id: item.docId,
                    payload: item.payload,
                    updated_at: new Date().toISOString()
                }, { onConflict: 'collection_name,doc_id' });

            if (error) {
                console.error(`[Offline Queue] Failed to sync ${item.collectionName}/${item.docId}:`, error);
                if (!isNetworkError(error)) {
                    // Non-network errors are removed to prevent infinite blocking loops
                    dequeueOfflineSync(item.collectionName, item.docId);
                }
                break; // Stop execution of next items if it was a network failure
            } else {
                console.log(`[Offline Queue] Successfully uploaded ${item.collectionName}/${item.docId}`);
                dequeueOfflineSync(item.collectionName, item.docId);
            }
        }
    } catch (e) {
        console.error("[Offline Queue] Exception during queue retry worker:", e);
    } finally {
        isSyncingQueue = false;
    }
};

// Set up global browser/Capacitor synchronization listeners
if (typeof window !== "undefined") {
    window.addEventListener("online", () => {
        console.log("[Network] Connectivity restored. Flushing offline queue...");
        syncOfflineQueue();
    });

    // Run interval check every 30 seconds for passive background recovery
    setInterval(() => {
        syncOfflineQueue();
    }, 30000);
}

/**
 * useSyncState (Supabase & Offline-First local storage)
 * Syncs a local state variable with Supabase using Realtime, falling back locally to prevent offline data loss.
 */
function getCachedOrInitial<T>(offlineKey: string, initialValue: T): T {
    try {
        if (typeof window !== "undefined") {
            const cached = localStorage.getItem(offlineKey);
            if (cached !== null) {
                const parsed = JSON.parse(cached);
                
                if (Array.isArray(initialValue)) {
                    const extracted = parsed.list !== undefined ? parsed.list : parsed;
                    return (Array.isArray(extracted) ? extracted : initialValue) as unknown as T;
                } else if (typeof initialValue === 'object' && initialValue !== null) {
                    return ((typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) ? parsed : initialValue) as unknown as T;
                } else {
                    return parsed as T;
                }
            }
        }
    } catch (e) {
        console.error(`[Offline Cache] Error loading initial fallback:`, e);
    }
    return initialValue;
}

export function useSyncState<T>(collectionName: string, docId: string, initialValue: T) {
    const offlineKey = `klyd_offline_${collectionName}_${docId}`;

    // 1. Initial State Initialization: Sychronously read local cached value first (Instant Offline Render)
    const [state, setState] = useState<T>(() => {
        return getCachedOrInitial(offlineKey, initialValue);
    });

    const [hasLoaded, setHasLoaded] = useState(false);
    const isRemoteUpdate = useRef(false);
    const isMounted = useRef(false);
    const lastSavedState = useRef<string>(safeStringify(state));
    const stateRef = useRef(state);

    const lastCollection = useRef(collectionName);
    const lastDoc = useRef(docId);

    // Reset state and loaded status synchronously during render if collection or document ID changes
    if (lastCollection.current !== collectionName || lastDoc.current !== docId) {
        lastCollection.current = collectionName;
        lastDoc.current = docId;
        const resetVal = getCachedOrInitial(offlineKey, initialValue);
        setState(resetVal);
        setHasLoaded(false);
        stateRef.current = resetVal;
        lastSavedState.current = safeStringify(resetVal);
    }

    useEffect(() => {
        stateRef.current = state;
    }, [state]);

    // 2. Fetch initial data and configure Supabase Realtime channel
    useEffect(() => {
        console.log(`[Supabase Sync] Switch to ${collectionName}/${docId}`);
        isMounted.current = true;
        setHasLoaded(false);
        isRemoteUpdate.current = false;

        // Try reading latest local cache again in case parameters changed
        try {
            const cached = localStorage.getItem(offlineKey);
            if (cached !== null) {
                const parsed = JSON.parse(cached);
                let cachedValue: any;
                if (Array.isArray(initialValue)) {
                    const extracted = parsed.list !== undefined ? parsed.list : parsed;
                    cachedValue = Array.isArray(extracted) ? extracted : initialValue;
                } else if (typeof initialValue === 'object' && initialValue !== null) {
                    cachedValue = (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) ? parsed : initialValue;
                } else {
                    cachedValue = parsed;
                }
                setState(cachedValue);
                lastSavedState.current = safeStringify(cachedValue);
            } else {
                setState(initialValue);
                lastSavedState.current = safeStringify(initialValue);
            }
        } catch (e) {
            console.error(`[Offline Cache] Error reading cache for ${docId}:`, e);
        }

        const fetchInitialData = async () => {
            try {
                // Check if this document has pending offline changes that are not synced yet
                const queueStr = localStorage.getItem("klyd_offline_sync_queue");
                const queue: SyncQueueItem[] = queueStr ? JSON.parse(queueStr) : [];
                const hasPendingSync = queue.some(item => item.collectionName === collectionName && item.docId === docId);

                // If offline changes are pending, skip initial fetch to prevent overwriting user modifications
                if (hasPendingSync) {
                    console.log(`[Supabase Sync] Local offline modifications are pending for ${docId}. Skipping remote fetch to preserve work.`);
                    setHasLoaded(true);
                    return;
                }

                const { data, error } = await supabase
                    .from('app_data')
                    .select('payload')
                    .eq('collection_name', collectionName)
                    .eq('doc_id', docId)
                    .limit(1)
                    .maybeSingle();

                if (!isMounted.current) return;

                if (error) {
                    console.error(`[Supabase Sync] Fetch error for ${docId}:`, error);
                    setHasLoaded(true);
                    return;
                }

                if (data && data.payload !== null && data.payload !== undefined) {
                    const fetchedData = data.payload;
                    let newValue: any;

                    if (Array.isArray(initialValue)) {
                        const extracted = fetchedData.list !== undefined ? fetchedData.list : fetchedData;
                        newValue = Array.isArray(extracted) ? extracted : initialValue;
                    } else if (typeof initialValue === 'object' && initialValue !== null) {
                        newValue = (typeof fetchedData === 'object' && fetchedData !== null && !Array.isArray(fetchedData)) ? fetchedData : initialValue;
                    } else {
                        newValue = fetchedData;
                    }

                    if (newValue !== undefined) {
                        const isRemoteEmpty = Array.isArray(newValue) ? newValue.length === 0 : Object.keys(newValue).length === 0;
                        const isLocalModified = safeStringify(stateRef.current) !== safeStringify(initialValue);

                        if (isRemoteEmpty && isLocalModified) {
                            console.warn(`[Supabase Sync] Server returned empty for ${docId}, but local state is modified. Preserving local.`);
                        } else {
                            console.log(`[Supabase Sync] Initial remote data loaded for ${docId}`);
                            isRemoteUpdate.current = true;
                            setState(newValue);
                            lastSavedState.current = safeStringify(newValue);
                            localStorage.setItem(offlineKey, safeStringify(newValue));
                        }
                    }
                } else {
                    console.log(`[Supabase Sync] Document ${collectionName}/${docId} does not exist yet.`);
                }
            } catch (err) {
                console.error(`[Supabase Sync] Exception during fetch Initial Data for ${docId}:`, err);
            } finally {
                setHasLoaded(true);
            }
        };

        fetchInitialData();

        // Subscribe to Supabase Realtime changes
        const channel = supabase.channel(`public:app_data:${collectionName}:${docId}`)
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'app_data',
                    filter: `collection_name=eq.${collectionName}`
                },
                (payload) => {
                    const newRecord = payload.new as any;
                    if (!newRecord || newRecord.doc_id !== docId || newRecord.collection_name !== collectionName) return;

                    // If we have pending offline sync for this, ignore realtime updates to prevent conflicts
                    const queueStr = localStorage.getItem("klyd_offline_sync_queue");
                    const queue: SyncQueueItem[] = queueStr ? JSON.parse(queueStr) : [];
                    if (queue.some(item => item.collectionName === collectionName && item.docId === docId)) {
                        console.log(`[Supabase Sync] Ignoring realtime update for ${docId} due to pending local offline modifications.`);
                        return;
                    }

                    const fetchedData = newRecord.payload;
                    let newValue: any;

                    if (Array.isArray(initialValue)) {
                        const extracted = fetchedData.list !== undefined ? fetchedData.list : fetchedData;
                        newValue = Array.isArray(extracted) ? extracted : initialValue;
                    } else if (typeof initialValue === 'object' && initialValue !== null) {
                        newValue = (typeof fetchedData === 'object' && fetchedData !== null && !Array.isArray(fetchedData)) ? fetchedData : initialValue;
                    } else {
                        newValue = fetchedData;
                    }

                    if (newValue !== undefined) {
                        console.log(`[Supabase Sync] Realtime update received for ${docId}`);
                        isRemoteUpdate.current = true;
                        setState(newValue);
                        lastSavedState.current = safeStringify(newValue);
                        localStorage.setItem(offlineKey, safeStringify(newValue));
                    }
                }
            )
            .subscribe();

        return () => {
            console.log(`[Supabase Sync] Cleanup ${docId}`);
            isMounted.current = false;
            supabase.removeChannel(channel);
        };
    }, [collectionName, docId]);

    // 3. Auto-save state changes locally and schedule background server sync
    useEffect(() => {
        if (!hasLoaded) return;

        if (isRemoteUpdate.current) {
            console.log(`[Supabase Sync] Local update for ${docId} skipped (matched remote)`);
            isRemoteUpdate.current = false;
            return;
        }

        if (!isMounted.current) return;

        const currentStateStr = safeStringify(state);
        if (currentStateStr === lastSavedState.current || currentStateStr === "") {
            return;
        }

        const sanitize = (obj: any): any => {
            if (Array.isArray(obj)) {
                return obj.map(sanitize);
            } else if (obj !== null && typeof obj === 'object') {
                return Object.fromEntries(
                    Object.entries(obj)
                        .filter(([_, v]) => v !== undefined)
                        .map(([k, v]) => [k, sanitize(v)])
                );
            }
            return obj;
        };

        // Instantly write to offline localStorage to guarantee data survival
        localStorage.setItem(offlineKey, currentStateStr);

        const timer = setTimeout(async () => {
            const rawPayload = Array.isArray(state) ? { list: state } : state;
            const payload = sanitize(rawPayload);
            if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
                const currentUserName = typeof window !== 'undefined' ? localStorage.getItem('klyd_current_user_name') : null;
                if (currentUserName) {
                    payload.updatedBy = currentUserName;
                }
            }

            // If offline, bypass network call immediately and enqueues changes
            if (typeof navigator !== "undefined" && !navigator.onLine) {
                console.log(`[Supabase Sync] Device is offline. Enqueueing sync for ${docId}.`);
                enqueueOfflineSync(collectionName, docId, payload);
                return;
            }

            console.log(`[Supabase Sync] Auto-saving ${docId} to Supabase...`);

            try {
                const { error } = await supabase
                    .from('app_data')
                    .upsert({
                        collection_name: collectionName,
                        doc_id: docId,
                        payload: payload,
                        updated_at: new Date().toISOString()
                    }, { onConflict: 'collection_name,doc_id' });

                if (error) {
                    console.error(`[Supabase Sync] Error during upsert of ${docId}:`, error);
                    if (isNetworkError(error)) {
                        console.log(`[Supabase Sync] Network issue detected. Enqueueing sync for ${docId}.`);
                        enqueueOfflineSync(collectionName, docId, payload);
                    }
                } else {
                    console.log(`[Supabase Sync] ${docId} saved successfully.`);
                    dequeueOfflineSync(collectionName, docId);
                    if (currentStateStr !== "") {
                        lastSavedState.current = currentStateStr;
                    }
                }
            } catch (err: any) {
                console.error(`[Supabase Sync] Network exception saving ${docId}:`, err);
                enqueueOfflineSync(collectionName, docId, payload);
            }
        }, 800); // 800ms debounce

        return () => clearTimeout(timer);
    }, [state, collectionName, docId, hasLoaded]);

    // 4. Manual force sync implementation (useful on important action triggers)
    const forceSync = async (overriddenState?: T) => {
        const stateToSave = overriddenState !== undefined ? overriddenState : stateRef.current;
        const rawPayload = Array.isArray(stateToSave) ? { list: stateToSave } : stateToSave;

        const sanitize = (obj: any): any => {
            if (Array.isArray(obj)) {
                return obj.map(sanitize);
            } else if (obj !== null && typeof obj === 'object') {
                return Object.fromEntries(
                    Object.entries(obj)
                        .filter(([_, v]) => v !== undefined)
                        .map(([k, v]) => [k, sanitize(v)])
                );
            }
            return obj;
        };

        const payload = sanitize(rawPayload);
        if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
            const currentUserName = typeof window !== 'undefined' ? localStorage.getItem('klyd_current_user_name') : null;
            if (currentUserName) {
                payload.updatedBy = currentUserName;
            }
        }
        const serialized = safeStringify(stateToSave);

        // Always save locally first
        localStorage.setItem(offlineKey, serialized);

        if (typeof navigator !== "undefined" && !navigator.onLine) {
            console.log(`[Supabase Sync] Offline during forceSync. Queueing ${docId}.`);
            enqueueOfflineSync(collectionName, docId, payload);
            return;
        }

        console.log(`[Supabase Sync] Force syncing ${docId} to Supabase...`);

        try {
            const { error } = await supabase
                .from('app_data')
                .upsert({
                    collection_name: collectionName,
                    doc_id: docId,
                    payload: payload,
                    updated_at: new Date().toISOString()
                }, { onConflict: 'collection_name,doc_id' });

            if (error) {
                if (isNetworkError(error)) {
                    enqueueOfflineSync(collectionName, docId, payload);
                } else {
                    throw error;
                }
            } else {
                console.log(`[Supabase Sync] ${docId} force-saved successfully.`);
                dequeueOfflineSync(collectionName, docId);
                lastSavedState.current = serialized;
            }
        } catch (e: any) {
            console.error(`[Supabase Sync] Exception during forceSync for ${docId}:`, e);
            enqueueOfflineSync(collectionName, docId, payload);
        }
    };

    return [state, setState, forceSync, hasLoaded] as const;
}
